import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Button,
  Container,
  Menu,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Link as ScrollLink } from 'react-scroll';
import logo from '../assets/Images/logo.png';

const pages = [
  { name: 'About', path: 'about' },
  { name: 'Timeline', path: 'timeline' },
  { name: 'Guidelines', path: 'guidelines' },
  { name: 'Workshops', path: 'workshops' },
  { name: 'Awards', path: 'awards' },
];

const years = [
  { year: '2025', href: 'https://adscai.cse.uom.lk/2025' },
];

function Navbar({ isNotificationVisible }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    if (open) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      {/* Navbar */}
      <AppBar
        position='fixed'
        sx={{
          backgroundColor: 'white',
          boxShadow: 'none',
          borderBottom: '1px solid #ddd',
          top: isNotificationVisible ? { xs: '50px', sm: '35px' } : '0',
          transition: 'top 0.3s ease-in-out',
          zIndex: 1500,
        }}
      >
        <Container maxWidth='xl'>
          <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img
                src={logo}
                alt='Logo'
                style={{ height: '40px', marginRight: '8px' }}
              />
            </Box>

            {/* Desktop Navbar Links */}
            <Box
              sx={{
                flexGrow: 1,
                display: { xs: 'none', md: 'flex' },
                justifyContent: 'center',
              }}
            >
              {pages.map((page) => (
                <Button
                  key={page.name}
                  component={ScrollLink}
                  to={page.path}
                  smooth={true}
                  duration={500}
                  offset={-70} // Adjust for fixed navbar height
                  sx={{
                    mx: 2,
                    color: 'black',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    fontSize: '1rem',
                    cursor: 'pointer',
                  }}
                >
                  {page.name}
                </Button>
              ))}

              {/* Year Dropdown */}
              <Button
                onClick={handleMenuOpen}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                  mx: 2,
                  color: 'black',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  fontSize: '1rem',
                  cursor: 'pointer',
                }}
              >
                Previous Editions
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'year-button',
                }}
              >
                {years.map(({ year, href }) => (
                  <MenuItem
                    key={year}
                    onClick={handleMenuClose}
                    component="a"
                    href={href}
                    sx={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    {year}
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            {/* Hamburger Icon (Right Side) */}
            <IconButton
              size='small'
              edge='end'
              onClick={handleDrawerToggle}
              sx={{ display: { xs: 'block', md: 'none' } }}
            >
              <MenuIcon sx={{ fontSize: '1.8rem', color: 'black' }} />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Sidebar (Mobile Menu) */}
      <Drawer
        anchor='right'
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{ '& .MuiDrawer-paper': { width: 300 }, zIndex: 2000 }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            padding: '8px',
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              color: 'black',
            }}
          >
            ✖
          </IconButton>

          <List sx={{ mt: 5, width: '100%', textAlign: 'center' }}>
            {pages.map((page) => (
              <ListItem
                button
                key={page.name}
                onClick={handleDrawerToggle}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '16px 0',
                  '&:hover': { backgroundColor: '#f0f0f0' },
                }}
              >
                <ListItemText>
                  <ScrollLink
                    to={page.path}
                    smooth={true}
                    duration={500}
                    offset={-70}
                    style={{
                      textDecoration: 'none',
                      color: 'black',
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                    }}
                  >
                    {page.name}
                  </ScrollLink>
                </ListItemText>
              </ListItem>
            ))}

            {/* Mobile Year Selection */}
            <ListItem
              button
              onClick={handleMenuOpen}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                padding: '16px 0',
                '&:hover': { backgroundColor: '#f0f0f0' },
              }}
            >
              <ListItemText
                primaryTypographyProps={{
                  style: { fontWeight: 'bold', fontSize: '1.1rem' },
                }}
                endIcon={<KeyboardArrowDownIcon />}
              >
                Previous Editions
              </ListItemText>
            </ListItem>
            {/* Mobile Year Dropdown */}
            {open && (
              years.map(({ year, href }) => (
                <ListItem
                  key={year}
                  component="a"
                  href={href}
                  onClick={handleMenuClose}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '8px 0',
                    '&:hover': { backgroundColor: '#f0f0f0' },
                  }}
                >
                  <ListItemText
                    primaryTypographyProps={{
                      style: { fontWeight: 'bold', fontSize: '1.1rem', color: '#777777' },
                    }}
                  >
                    {year}
                  </ListItemText>
                </ListItem>
              ))
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
}

export default Navbar;
